import './info-card.scss';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  Card,
  CardActions,
  CardContent,
  Collapse,
  IconButton,
  IconButtonProps,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import { DateTime } from 'luxon';
import React, { useState } from 'react';
import styled from 'styled-components';
import { reemaColors, themeColors } from '../styles/colors';

import { IconAvatar, IconAvatarType } from '@act/storybook';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// ICONS =======================================================================
import { timeFormatter } from '@act/shared/utilities';
import { FlexContainer, SmartContentDisplay } from '@act/storybook';

export type FeedCardAction = {
  icon?: string | JSX.Element;
  title: string | JSX.Element;
  details?: string;
  handler: () => void;
};

export type AdditionalFeedCardActions = Pick<
  FeedCardAction,
  'title' | 'details' | 'handler' | 'icon'
>;

export const InfoCardStyled = styled(Card)<{
  'background-style': BackgroundStyles;
}>`
  box-sizing: border-box;
  font-size: 12px;
  ${props =>
    props['background-style'] === 'shadow' &&
    `
      background: linear-gradient(
      0deg,
      rgba(0, 103, 128, 0.08),
      rgba(0, 103, 128, 0.08)
    ), ${reemaColors.lightBackground};
    `}
  ${props =>
    props['background-style'] === 'start' &&
    `
      background: ${reemaColors.primaryContainer};
    `}
  ${props =>
    props['background-style'] === 'completed' &&
    `
      background: ${reemaColors.successBackground};
    `}
    ${props =>
    props['background-style'] === 'unscheduled' &&
    `
      background: ${reemaColors.removedBackground};
    `}
`;

const CardHeader = styled.div`
  display: grid;
  font-size: 16px;
  grid-template-columns: 48px 1fr auto;
  gap: 12px;
  margin: 16px;
  .content {
    min-width: 0;
  }
  .actions {
    align-self: start;
  }
  .title {
    display: grid;
    align-items: center;
    grid-template-columns: auto auto 1fr;
    gap: 8px;
    h4 {
      margin: 0;
      font-weight: 500;
      font-size: 16px;
    }
  }
  p {
    margin: 4px 0 0;
    line-height: 20px;
    font-size: 14px;
    overflow-wrap: break-word;
    word-break: break-word;
  }
`;
export const StyledCardContent = styled(CardContent)`
  font-size: 14px;
  padding: 8px 16px;
  overflow-wrap: break-word;
  word-break: break-word;
  .info-pane {
    font-size: 16px;
    padding: 16px 20px;
    border-radius: 6px;
    border: 1px solid #dce4e8;
    h6 {
      color: #70787c;
      margin: 12px 0 0;
      &:first-child {
        margin: 0;
      }
    }
    p {
      margin: 0 0 4px;
    }
  }
  p {
    &:first-child {
      margin-top: 0;
    }
    &:last-child {
      margin-bottom: 0;
    }
  }
`;
const ActionGrid = styled.div`
  display: grid;
  gap: 8px;
  margin: 0px;
  grid-template-columns: auto 1fr auto;
`;

const StyledCardActions = styled(CardActions)``;

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

export const ExpandMore = styled((props: ExpandMoreProps) => {
  const { ...other } = props;
  other.expand = undefined;
  return <IconButton {...other} />;
})(({ expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  transition: 'transform 0.3s',
  color: themeColors.onSurfaceVariant,
}));

export type BackgroundStyles =
  | 'default'
  | 'shadow'
  | 'start'
  | 'completed'
  | 'removed'
  | 'unscheduled';

export interface InfoCardProps {
  children?: React.ReactElement;
  expandedContent?: React.ReactElement;
  expandableHeaderContent?: React.ReactElement;
  expandableHeaderLabel?: string;
  shouldHighlight?: boolean;
  menuItems?: AdditionalFeedCardActions[];
  subject: string | React.ReactElement;
  startTime: number;
  endTime?: number;
  timeDisplayOverride?: string | null;
  timeAsDue?: boolean;
  includeTime?: boolean;
  statusChips?: React.ReactElement[];
  content?: React.ReactElement;
  primaryOption?: React.ReactElement;
  secondaryOption?: React.ReactElement;
  additionalAction?: React.ReactElement;
  backgroundStyle?: BackgroundStyles;
  variant:
    | 'visit'
    | 'call'
    | 'message'
    | 'resource'
    | 'reminder'
    | 'note'
    | 'transfer'
    | 'task'
    | 'change'
    | 'hospitalization'
    | 'member-progressed'
    | 'appointment';
  subVariant?: 'incoming' | 'outgoing' | 'attended' | 'missed' | 'cancelled';
}

interface VariantOptions {
  icon: React.ReactElement;
  iconColor: string;
}

const InfoCard = ({
  children,
  expandedContent,
  expandableHeaderContent,
  expandableHeaderLabel,
  statusChips,
  subject,
  backgroundStyle = 'default',
  includeTime = true,
  timeAsDue = false,
  startTime,
  endTime,
  timeDisplayOverride,
  variant,
  subVariant,
  secondaryOption,
  primaryOption,
  additionalAction,
  menuItems,
  shouldHighlight,
}: InfoCardProps) => {
  const theme = useTheme();
  const [expandedHeader, setExpandedHeader] = React.useState(false);

  const [additionalMenuAnchor, setAdditionalMenuAnchor] =
    useState<null | HTMLElement>(null);
  const open = Boolean(additionalMenuAnchor);
  const openAdditionalMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAdditionalMenuAnchor(event.currentTarget);
  };
  const closeAdditionalMenu = () => {
    setAdditionalMenuAnchor(null);
  };
  function executeHandler(handler: () => void) {
    setAdditionalMenuAnchor(null);
    handler();
  }
  const handleExpandedHeaderClick = () => {
    setExpandedHeader(!expandedHeader);
  };

  const start = startTime ? DateTime.fromMillis(startTime) : null;
  let time: string = '';
  if (timeDisplayOverride != null && timeDisplayOverride.trim() !== '') {
    time = timeDisplayOverride.trim();
  } else if (startTime) {
    if (endTime) {
      time = timeFormatter.durationDateString(startTime, endTime);
    } else {
      time = timeFormatter.getDateString({ date: start, includeTime });
    }
  }

  if (timeAsDue && time !== '') {
    time = `Due ${time}`;
  }

  const showCardOptions =
    !!expandedContent || !!primaryOption || !!secondaryOption;

  function getAvatarType(variant: string, subVariant: string) {
    const infoType = ['call', 'appointment'].includes(variant)
      ? `${subVariant ? `${subVariant}_` : ''}${variant}`
      : variant;
    return infoType.replace(' ', '_').toLocaleLowerCase() as IconAvatarType;
  }

  return (
    <InfoCardStyled
      variant="outlined"
      className={`info-card ${shouldHighlight ? 'highlight' : ''}`}
      {...{ 'background-style': backgroundStyle }}
    >
      <CardHeader>
        <IconAvatar avatarType={getAvatarType(variant, subVariant)} />
        <div className="content">
          <FlexContainer
            direction="row"
            height="fit-content"
            gap={1}
            alignItems="center"
            flexWrap
          >
            <Typography variant="titleMedium" sx={{ flexShrink: 0 }}>
              {subject}
            </Typography>
            {statusChips?.length > 0 && (
              <FlexContainer
                direction="row"
                height="fit-content"
                gap={1}
                flexWrap
              >
                {statusChips.map((chip, i) => {
                  return <div key={`chip-${i}`}>{chip}</div>;
                })}
              </FlexContainer>
            )}
          </FlexContainer>
          <p>{time}</p>
          <div>
            {expandableHeaderContent && (
              <FlexContainer direction="row">
                {expandableHeaderLabel && (
                  <Typography
                    sx={{ cursor: 'pointer' }}
                    variant="bodySmall"
                    onClick={handleExpandedHeaderClick}
                  >
                    {expandableHeaderLabel}
                  </Typography>
                )}
                <ExpandMore
                  expand={expandedHeader}
                  onClick={handleExpandedHeaderClick}
                  aria-expanded={expandedHeader}
                  aria-label="show more"
                >
                  <ExpandMoreIcon
                    sx={{
                      color: themeColors.onSurfaceVariant,
                      width: 16,
                      height: 16,
                    }}
                  />
                </ExpandMore>
              </FlexContainer>
            )}
          </div>
        </div>
        {menuItems?.length > 0 && (
          <div className="actions">
            <IconButton
              aria-label="settings"
              onClick={openAdditionalMenu}
              sx={{ margin: '-8px', color: themeColors.onSurfaceVariant }}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={additionalMenuAnchor}
              open={open}
              onClose={closeAdditionalMenu}
            >
              {menuItems.map((item, index) => (
                <MenuItem
                  onClick={() => {
                    executeHandler(item.handler);
                  }}
                  key={`menu-item-${index}`}
                >
                  {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
                  <ListItemText>{item.title}</ListItemText>
                </MenuItem>
              ))}
            </Menu>
          </div>
        )}
      </CardHeader>

      {expandableHeaderContent && (
        <Collapse in={expandedHeader} timeout="auto" unmountOnExit>
          <div className="expandable-header">
            <StyledCardContent
              style={{
                margin: '0 16px',
                borderRadius: '8px',
                backgroundColor: theme.palette.navyShades[50],
              }}
            >
              {expandableHeaderContent}
            </StyledCardContent>
          </div>
        </Collapse>
      )}

      {children && <StyledCardContent>{children}</StyledCardContent>}
      {expandedContent && (
        <StyledCardContent>
          <SmartContentDisplay>{expandedContent}</SmartContentDisplay>
        </StyledCardContent>
      )}
      {showCardOptions && (
        <ActionGrid>
          <StyledCardActions disableSpacing>
            {primaryOption}
            {secondaryOption}
          </StyledCardActions>
          {additionalAction && (
            <Box sx={{ display: 'flex' }}>{additionalAction}</Box>
          )}
        </ActionGrid>
      )}
    </InfoCardStyled>
  );
};

export default InfoCard;
